import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import {
  getAuth,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithEmailAndPassword,
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyAdgZvYpLnE5jnuXbyHTwLPdPddfzw6Qx0",
  authDomain: "dns-project-ee42b.firebaseapp.com",
  projectId: "dns-project-ee42b",
  storageBucket: "dns-project-ee42b.firebasestorage.app",
  messagingSenderId: "309016337966",
  appId: "1:309016337966:web:ef733b61af92c30f3389bf",
  measurementId: "G-6LCPKJ22FZ"
};

const app = initializeApp(firebaseConfig);

// Firebase Authentication（メール確認・パスワードリセットのメール送信に使用）
const auth = getAuth(app);
// メール文面・Firebase既定の承認ページを日本語で表示する
auth.languageCode = 'ja';

let messaging = null;
try {
  messaging = getMessaging(app);
} catch (e) {
  console.warn('FCM not supported in this browser');
}

/**
 * FCMトークンを取得してバックエンドに登録する
 */
export async function requestFCMToken() {
  if (!messaging) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('通知の許可が得られませんでした');
      return null;
    }

    // FCM 専用の Service Worker を明示的に登録して getToken に渡す。
    // これをしないと、アプリ側で登録している /sw.js に上書き・競合され、
    // バックグラウンドのプッシュ通知（firebase-messaging-sw.js の onBackgroundMessage）が
    // 動作しないことがある。専用 SW を明示指定して確実に使わせる。
    let swRegistration = undefined;
    try {
      if ('serviceWorker' in navigator) {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/firebase-cloud-messaging-push-scope',
        });
        // 有効化を待つ（初回登録直後は installing/waiting のことがある）
        await navigator.serviceWorker.ready;
      }
    } catch (swErr) {
      console.warn('firebase-messaging-sw.js の登録に失敗:', swErr);
    }

    // VAPID key は Firebase Console > Cloud Messaging > Web Push certificates から取得
    const token = await getToken(messaging, {
      vapidKey: 'BFW_7YE5ZWYONHZ3Cz4Kae6JoGGHNjbRPxQdgcrej2JkZQtPgyMxvlMjMFEyCg7XL1KLkzOrTC9tgW1H40xf2yw',
      ...(swRegistration ? { serviceWorkerRegistration: swRegistration } : {}),
    });

    if (token) {
      console.log('FCM Token:', token.substring(0, 20) + '...');
      localStorage.setItem('fcmToken', token);
      // バックエンドに登録
      await registerTokenOnServer(token);
      return token;
    }
  } catch (error) {
    console.error('FCMトークン取得エラー:', error);
  }
  return null;
}

/**
 * FCMトークンをバックエンドに保存
 */
async function registerTokenOnServer(token) {
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    await fetch('/api/notifications/fcm-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ token })
    });
  } catch (error) {
    console.error('FCMトークン登録エラー:', error);
  }
}

/**
 * フォアグラウンドメッセージリスナー
 * アプリが開いている時にプッシュ通知を受け取った場合のコールバック
 */
export function onForegroundMessage(callback) {
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    console.log('FCM foreground message:', payload);
    callback(payload);
  });
}

/**
 * パスワードリセットメールを送信する（Firebase が独自ドメイン設定に従って送信）
 * @param {string} email 送信先メールアドレス
 */
// メールアクション完了後に遷移させる自前の日本語ページ
const ACTION_CONTINUE_URL = 'https://deli-ns.com/auth/action';

export async function sendPasswordReset(email) {
  // 送信直前に言語を明示設定（メール文面と承認ページの lang=ja を確実に付与）
  auth.languageCode = 'ja';
  // 確認完了後に自前の日本語ページへ遷移させる。
  // deli-ns.com は Firebase の承認済みドメインに登録済みのため 400 にならない。
  await sendPasswordResetEmail(auth, email, {
    url: ACTION_CONTINUE_URL,
    handleCodeInApp: false,
  });
}

/**
 * 新規登録直後にメール確認リンクを送信する。
 * バックエンドの独自認証とは別に、メール送信のためだけに Firebase へサインインして送る。
 * @param {string} email
 * @param {string} password 登録時のパスワード
 */
export async function sendVerificationEmail(email, password) {
  // メール送信には Firebase 上でサインイン済みの User オブジェクトが必要
  const cred = await signInWithEmailAndPassword(auth, email, password);
  if (cred?.user && !cred.user.emailVerified) {
    // 送信直前に言語を明示設定（メール文面と承認ページの lang=ja を確実に付与）
    auth.languageCode = 'ja';
    // 確認完了後に自前の日本語ページ(/auth/action)へ遷移させる
    await sendEmailVerification(cred.user, {
      url: ACTION_CONTINUE_URL,
      handleCodeInApp: false,
    });
  }
  // メール送信目的のサインインなので、以降のセッションは既存のアプリ認証に委ねる
  try {
    await auth.signOut();
  } catch (e) {
    // サインアウト失敗は致命的ではない
  }
}

// 自前の承認ページ(/auth/action)で oobCode を直接処理するための関数群。
// メールリンクに含まれる oobCode を渡して各アクションを実行する。
export async function applyEmailVerification(oobCode) {
  auth.languageCode = 'ja';
  await applyActionCode(auth, oobCode);
}

export async function checkOobCode(oobCode) {
  return await checkActionCode(auth, oobCode);
}

export async function verifyResetCode(oobCode) {
  return await verifyPasswordResetCode(auth, oobCode);
}

export async function confirmReset(oobCode, newPassword) {
  await confirmPasswordReset(auth, oobCode, newPassword);
}

export { app, auth, messaging };
